import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Pencil, Plus, Trash2, UserPlus } from "lucide-react";

const SLOTS = [["TO", "To"], ["CC", "CC"], ["OFF", "Skip"]];
const err = (e, m) => toast.error(e.response?.data?.detail || m);

const SlotToggle = ({ value, onChange, testId }) => (
  <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-slate-200" role="radiogroup" data-testid={testId}>
    {SLOTS.map(([k, l]) => (
      <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
        className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${value === k ? (k === "OFF" ? "bg-slate-200 text-slate-700" : "bg-slate-900 text-white") : "bg-white text-slate-500 hover:bg-slate-50"}`}>{l}</button>
    ))}
  </div>
);

const ContactRow = ({ c, onSlot, onDelete }) => (
  <li className={`flex items-center gap-3 px-3 py-2 ${c.default === "OFF" ? "opacity-60" : ""}`} data-testid={`os-contact-${c.email}`}>
    <div className="min-w-0 flex-1">
      <div className="truncate text-[12px] font-medium text-slate-900">{c.name || c.email}{c.role && <span className="ml-1.5 text-[11px] font-normal text-slate-500">· {c.role}</span>}</div>
      {c.name && <div className="truncate font-mono text-[11px] text-slate-500">{c.email}</div>}
    </div>
    <SlotToggle value={c.default} onChange={(v) => onSlot(c, v)} testId={`os-contact-slot-${c.email}`} />
    <button type="button" onClick={() => onDelete(c)} className="text-slate-400 hover:text-rose-600" aria-label={`Remove ${c.email}`} data-testid={`os-contact-del-${c.email}`}><Trash2 className="h-3.5 w-3.5" /></button>
  </li>
);

function AddContact({ customerName, onAdded }) {
  const [f, setF] = useState({ name: "", email: "", role: "" });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/finance-outstanding/reminder-contacts", { customer_name: customerName, ...f, default: "TO" }); setF({ name: "", email: "", role: "" }); onAdded(data); }
    catch (x) { err(x, "Could not add contact"); }
    setBusy(false);
  };
  return (
    <form onSubmit={add} className="grid gap-2 border-t border-slate-100 px-3 py-2 sm:grid-cols-[1fr_1.3fr_1fr_auto]" data-testid="os-contact-add">
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className="h-8 text-[12px]" data-testid="os-contact-add-name" />
      <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="email@company.com" type="email" required className="h-8 text-[12px]" data-testid="os-contact-add-email" />
      <Input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="Role (e.g. AP, Controller)" className="h-8 text-[12px]" data-testid="os-contact-add-role" />
      <Button type="submit" size="sm" disabled={busy || !f.email} data-testid="os-contact-add-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add</Button>
    </form>
  );
}

function AutoCopies({ customerName, auto, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [fin, setFin] = useState("");
  const [owner, setOwner] = useState("");
  const open = () => { setFin(auto.finance_team_emails.join(", ")); setOwner(auto.sales_owner_email); setEdit(true); };
  const save = async () => {
    try { const { data } = await api.put("/finance-outstanding/reminder-settings", { customer_name: customerName, finance_team_emails: fin.split(/[,;\s]+/).filter(Boolean), sales_owner_email: owner }); onSaved(data); setEdit(false); toast.success("Automatic copies saved"); }
    catch (x) { err(x, "Could not save"); }
  };
  if (edit) return (
    <div className="grid gap-2 border-t border-slate-100 bg-slate-50 px-3 py-2 sm:grid-cols-[1fr_1fr_auto]" data-testid="os-auto-edit">
      <label className="text-[11px] text-slate-500">Finance team (every customer)<Input value={fin} onChange={(e) => setFin(e.target.value)} className="mt-0.5 h-8 text-[12px]" data-testid="os-auto-finance" /></label>
      <label className="text-[11px] text-slate-500">Sales owner (this customer)<Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-0.5 h-8 text-[12px]" data-testid="os-auto-owner" /></label>
      <div className="flex items-end gap-1"><Button size="sm" onClick={save} data-testid="os-auto-save">Save</Button><Button size="sm" variant="ghost" onClick={() => setEdit(false)}>Cancel</Button></div>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 bg-slate-50 px-3 py-2 text-[11px]" data-testid="os-auto-cc">
      <span className="text-slate-500">Always copied:</span>
      {auto.list.map((a) => <span key={a.email} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5" data-testid={`os-auto-${a.email}`}><Lock className="h-3 w-3 text-slate-400" /><b className="font-medium">{a.label}</b><span className="font-mono text-slate-600">{a.email}</span></span>)}
      {!auto.sales_owner_email && <span className="text-amber-700">No sales owner set</span>}
      <button type="button" onClick={open} className="ml-auto inline-flex items-center gap-1 text-slate-500 hover:text-slate-900" data-testid="os-auto-edit-btn"><Pencil className="h-3 w-3" />Edit</button>
    </div>
  );
}

export default function ReminderContacts({ customerName, onChange }) {
  const [d, setD] = useState(null);
  const emit = useCallback((cs) => onChange({ to: cs.filter((c) => c.default === "TO").map((c) => c.email), cc: cs.filter((c) => c.default === "CC").map((c) => c.email) }), [onChange]);
  useEffect(() => {
    api.get("/finance-outstanding/reminder-contacts", { params: { customer_name: customerName } }).then(({ data }) => { setD(data); emit(data.contacts); }).catch((x) => err(x, "Could not load contacts"));
  }, [customerName]); // eslint-disable-line react-hooks/exhaustive-deps
  const setContacts = (cs) => { setD((x) => ({ ...x, contacts: cs })); emit(cs); };
  const slot = async (c, v) => {
    const prev = d.contacts;
    setContacts(prev.map((x) => (x.id === c.id ? { ...x, default: v } : x)));
    try { await api.patch(`/finance-outstanding/reminder-contacts/${c.id}`, { default: v }); } catch (x) { setContacts(prev); err(x, "Could not update"); }
  };
  const del = async (c) => {
    try { await api.delete(`/finance-outstanding/reminder-contacts/${c.id}`); setContacts(d.contacts.filter((x) => x.id !== c.id)); } catch (x) { err(x, "Could not remove"); }
  };
  if (!d) return <div className="rounded-lg border border-slate-200 p-3 text-[12px] text-slate-500">Loading contacts…</div>;
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="os-contacts">
      <div className="flex items-center gap-2 px-3 py-2 text-[12px]">
        <UserPlus className="h-4 w-4 text-slate-500" /><b>Recipients</b>
        <span className="text-slate-500" data-testid="os-contacts-count">{d.contacts.filter((c) => c.default === "TO").length} To · {d.contacts.filter((c) => c.default === "CC").length + d.list.length} CC · saved for {customerName}</span>
      </div>
      {d.contacts.length ? <ul className="divide-y divide-slate-100 border-t border-slate-100">{d.contacts.map((c) => <ContactRow key={c.id} c={c} onSlot={slot} onDelete={del} />)}</ul>
        : <div className="border-t border-slate-100 px-3 py-2 text-[12px] text-rose-700" data-testid="os-reminder-no-recipient">No contacts yet — add the customer's Accounts Payable team below.</div>}
      <AddContact customerName={customerName} onAdded={(c) => setContacts([...d.contacts, c])} />
      <AutoCopies customerName={customerName} auto={d} onSaved={(a) => setD((x) => ({ ...x, ...a }))} />
    </div>
  );
}
